import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, Calendar, User, MessageSquare, ThumbsUp, Send, Mail, CheckCircle, ExternalLink, Twitter, Linkedin, Link2 } from "lucide-react";
import { getRelatedPosts } from "../data/blogPosts";
import { useTranslation } from 'react-i18next';

// ── Share Buttons ────────────────────────────────────────────────────
function ShareButtons({ title, url }) {
  const { t } = useTranslation();
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  return (
    <div className="flex items-center gap-2 sm:gap-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Share:</span>
      <a href={"https://twitter.com/intent/tweet?text=" + encodedTitle + "&url=" + encodedUrl} target="_blank" rel="noopener noreferrer" className="p-2 border border-zinc-800 hover:border-amber-500 text-zinc-400 hover:text-amber-500 transition-colors" aria-label="Share on Twitter">
        <Twitter className="w-4 h-4" />
      </a>
      <a href={"https://www.linkedin.com/sharing/share-offsite/?url=" + encodedUrl} target="_blank" rel="noopener noreferrer" className="p-2 border border-zinc-800 hover:border-amber-500 text-zinc-400 hover:text-amber-500 transition-colors" aria-label="Share on LinkedIn">
        <Linkedin className="w-4 h-4" />
      </a>
      <button onClick={() => navigator.clipboard && navigator.clipboard.writeText(url)} className="p-2 border border-zinc-800 hover:border-amber-500 text-zinc-400 hover:text-amber-500 transition-colors" aria-label="Copy link">
        <Link2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Table of Contents ────────────────────────────────────────────────
function TableOfContents({ content }) {
  const { t } = useTranslation();
  const [tocOpen, setTocOpen] = useState(false);
  if (content == null) return null;
  if (typeof content !== "string") return null;
  var headings = content.split("\n").filter(function(line) { return line.trim().startsWith("## "); }).map(function(line) { return line.trim().replace("## ", ""); });

  if (headings.length < 2) return null;

  return (
    <nav className="border border-zinc-900 p-4 sm:p-6 mb-6 sm:mb-8" aria-label="Table of Contents">
      <button type="button" onClick={() => setTocOpen(!tocOpen)} className="flex items-center justify-between w-full lg:cursor-default" aria-expanded={tocOpen}>
        <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">Table of Contents</h3>
        <span className="lg:hidden text-zinc-500">{tocOpen ? "−" : "+"}</span>
      </button>
      <ol className={"space-y-2 mt-4 " + (tocOpen ? "block" : "hidden") + " lg:block"}>
        {headings.map(function(h, i) {
          return (
            <li key={i}>
              <a href={"#section-" + i} onClick={() => setTocOpen(false)} className="text-sm text-zinc-400 hover:text-amber-500 transition-colors">{h}</a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ── Newsletter Inline CTA ────────────────────────────────────────────
function NewsletterInlineCTA() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = function(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="border border-emerald-500/30 bg-emerald-500/5 p-6 my-8 flex items-center gap-3" role="alert">
        <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
        <p className="text-emerald-400 text-sm">You're in! Check your inbox for a confirmation email.</p>
      </div>
    );
  }

  return (
    <div className="border border-amber-500/20 bg-amber-500/5 p-4 sm:p-6 my-6 sm:my-8">
      <div className="flex items-start gap-3">
        <Mail className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-display text-sm uppercase text-white mb-1">Enjoying this article?</h4>
          <p className="text-zinc-400 text-xs leading-relaxed mb-3">Get weekly AI agent insights delivered to your inbox.</p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
            <input type="email" value={email} onChange={function(e) { setEmail(e.target.value); }} placeholder="your@email.com" required className="flex-1 bg-black/50 border border-zinc-800 px-3 py-2 text-sm text-white placeholder-zinc-600 font-mono outline-none focus:border-amber-500 transition-colors min-h-[44px]" />
            <button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 font-bold uppercase tracking-wider text-[10px] transition-colors shrink-0 min-h-[44px]">Subscribe</button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Inline Product CTA ───────────────────────────────────────────────
function InlineCTA() {
  return (
    <div className="border border-amber-500/30 bg-amber-500/5 p-5 sm:p-8 my-8 sm:my-12 text-center">
      <h3 className="font-display text-lg sm:text-xl uppercase text-white mb-2">Ready to deploy your AI agent?</h3>
      <p className="text-zinc-400 text-sm mb-6 max-w-lg mx-auto">Start your free 7-day trial. No credit card required. Deploy in 5 minutes.</p>
      <Link to="/pricing" className="inline-block bg-amber-500 hover:bg-amber-400 text-black px-8 py-3 font-bold uppercase tracking-wider text-xs transition-colors">Start Free Trial</Link>
    </div>
  );
}

// ── Comment Section ──────────────────────────────────────────────────
function CommentSection() {
  const { t } = useTranslation();
  const [comments, setComments] = useState([
    { id: 1, name: "Alex M.", date: "2 hours ago", text: "Great article! We deployed AI agents for our support team last month and already seeing 50% reduction in response time.", likes: 12 },
    { id: 2, name: "Sarah K.", date: "5 hours ago", text: "The ROI breakdown is really helpful. We were on the fence about AI agents but this convinced us to start the trial.", likes: 8 },
  ]);
  const [newComment, setNewComment] = useState("");
  const [likedComments, setLikedComments] = useState(new Set());

  const handleSubmit = function(e) {
    e.preventDefault();
    if (!newComment.trim()) return;
    setComments([{ id: Date.now(), name: "Guest", date: "Just now", text: newComment.trim(), likes: 0 }].concat(newComment));
    setNewComment("");
  };

  const handleLike = function(id) {
    if (likedComments.has(id)) return;
    setLikedComments(new Set(Array.from(likedComments).concat([id])));
    setComments(comments.map(function(c) { return c.id === id ? Object.assign({}, c, { likes: c.likes + 1 }) : c; }));
  };

  return (
    <section className="mt-12 border-t border-zinc-900 pt-8" aria-label="Comments">
      <h3 className="flex items-center gap-2 font-display text-lg uppercase text-white mb-6">
        <MessageSquare className="w-5 h-5 text-amber-500" />Discussion ({comments.length})
      </h3>
      <form onSubmit={handleSubmit} className="mb-8">
        <textarea value={newComment} onChange={function(e) { setNewComment(e.target.value); }} placeholder="Share your thoughts..." rows={3} className="w-full bg-black/50 border border-zinc-800 p-4 text-sm text-white placeholder-zinc-600 font-mono outline-none focus:border-amber-500 transition-colors resize-none" />
        <div className="flex justify-end mt-3">
          <button type="submit" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 font-bold uppercase tracking-wider text-[10px] transition-colors">
            <Send className="w-3 h-3" /> Post Comment
          </button>
        </div>
      </form>
      <div className="space-y-6">
        {comments.map(function(comment) {
          return (
            <div key={comment.id} className="border border-zinc-900 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-zinc-800 flex items-center justify-center"><User className="w-4 h-4 text-zinc-400" /></div>
                  <span className="font-mono text-xs text-white">{comment.name}</span>
                  <span className="font-mono text-[9px] text-zinc-500">{comment.date}</span>
                </div>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed mb-3">{comment.text}</p>
              <button onClick={() => handleLike(comment.id)} className={"flex items-center gap-1 text-xs transition-colors " + (likedComments.has(comment.id) ? "text-amber-500" : "text-zinc-500 hover:text-amber-500")}>
                <ThumbsUp className="w-3 h-3" />{comment.likes}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Estimated Reading Time helper ────────────────────────────────────
function estimateReadingTime(content) {
  if (content == null) return "1 min read";
  var text = typeof content === "string" ? content : String(content);
  var words = text.trim().split(/\s+/).filter(function(w) { return w.length > 0; }).length;
  var minutes = Math.max(1, Math.ceil(words / 200));
  return minutes + " min read";
}

// ── Main ArticleLayout ───────────────────────────────────────────────
export default function ArticleLayout({ post, slug, content: contentProp }) {
  var t = useTranslation().t;

  if (!post) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl uppercase mb-4">Article Not Found</h1>
          <p className="text-zinc-400 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-mono text-xs uppercase tracking-[0.2em]">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  var postContent = contentProp || post.content || "";
  if (postContent === null || postContent === undefined) postContent = "";

  var relatedPosts = getRelatedPosts(slug, 6);
  var articleUrl = "https://maxemployee.com/blog/" + slug;
  var readingTime = post.readTime || estimateReadingTime(postContent);

  // Content renderer
  function renderContent(content) {
    if (content == null) return null;
    var text = typeof content === "string" ? content : String(content);
    if (!text || !text.trim()) return null;
    var lines = text.trim().split("\n");
    var elements = [];
    var h2Index = 0;
    var paragraphCount = 0;
    var newsletterInjected = false;

    for (var i = 0; i < lines.length; i++) {
      var line = lines[i].trim();
      if (line === "") continue;

      // H2
      if (line.startsWith("## ")) {
        var headingText = line.replace("## ", "");
        elements.push(<h2 key={"h2-" + i} id={"section-" + h2Index} className="font-display text-2xl md:text-3xl uppercase text-white mt-12 mb-6 leading-tight">{headingText}</h2>);
        h2Index++;
        continue;
      }

      // H3
      if (line.startsWith("### ")) {
        elements.push(<h3 key={"h3-" + i} className="font-display text-xl uppercase text-white mt-8 mb-4">{line.replace("### ", "")}</h3>);
        continue;
      }

      // Bold text processing
      var processInline = function(txt) {
        var parts = txt.split(/(\*\*[^*]+\*\*)/g);
        return parts.map(function(part, pi) {
          if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={pi} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
          }
          return part;
        });
      };

      // List items
      if (line.startsWith("- ")) {
        elements.push(<li key={"li-" + i} className="text-zinc-400 leading-relaxed ml-4 mb-2 list-disc list-inside">{processInline(line.replace("- ", ""))}</li>);
        continue;
      }

      // Regular paragraph
      paragraphCount++;
      elements.push(<p key={"p-" + i} className="text-zinc-400 leading-relaxed mb-4">{processInline(line)}</p>);

      // Inject newsletter CTA after 2nd paragraph
      if (paragraphCount === 2 && !newsletterInjected) {
        elements.push(<NewsletterInlineCTA key="newsletter-inline" />);
        newsletterInjected = true;
      }
    }

    return elements;
  }

  return (
    <article>
      {/* Hero */}
      <div className="relative border-b border-zinc-900">
        <div className="absolute inset-0">
          <img src={post.ogImage} alt={post.title} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/60 via-[#09090B]/80 to-[#09090B]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 pt-8 sm:pt-12 pb-10 sm:pb-16">
          <Link to="/blog" className="inline-flex items-center gap-2 text-zinc-400 hover:text-amber-500 font-mono text-xs uppercase tracking-[0.2em] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="bg-amber-500/10 border border-amber-500/30 px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-amber-500">
              {post.category || post.tag}
            </span>
            {post.tags && post.tags.slice(0, 3).map(function(tag) {
              return <span key={tag} className="px-2 py-1 border border-zinc-800 text-[9px] font-mono uppercase tracking-wider text-zinc-500">{tag}</span>;
            })}
          </div>

          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl uppercase leading-[0.95] text-white mb-4 sm:mb-6">
            {post.title}
          </h1>

          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-3xl mb-6 sm:mb-8">
            {post.excerpt}
          </p>

          <div className="flex flex-wrap items-center gap-4">
            {post.author && (
              <Link to={"/blog/author/" + encodeURIComponent(post.author.name)} className="flex items-center gap-2 text-zinc-400 hover:text-amber-500 transition-colors">
                <User className="w-4 h-4" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em]">{post.author.name}</span>
              </Link>
            )}
            <div className="flex items-center gap-2 text-zinc-400">
              <Calendar className="w-4 h-4" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
                {new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
              </span>
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <Clock className="w-4 h-4" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">{readingTime}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <TableOfContents content={postContent} />
            <div className="prose-custom">
              {renderContent(postContent)}
            </div>

            <InlineCTA />

            <div className="border-t border-zinc-900 pt-6 mt-8">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <ShareButtons title={post.title} url={articleUrl} />
              </div>
            </div>

            {/* Author Box placeholder - simplified */}
            {post.author && (
              <div className="border border-zinc-900 p-6 mt-8">
                <div className="flex items-center gap-3 mb-3">
                  <User className="w-8 h-8 text-zinc-400" />
                  <div>
                    <p className="font-mono text-sm text-white">{post.author.name}</p>
                    <p className="font-mono text-[10px] text-zinc-500">{post.author.role}</p>
                  </div>
                </div>
                <p className="text-zinc-400 text-sm">{post.author.bio}</p>
              </div>
            )}

            <CommentSection />
          </div>

          <div className="lg:col-span-4 hidden" />
        </div>
      </div>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="border-t border-zinc-900 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-16">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">// RELATED ARTICLES</div>
            <h2 className="font-display text-2xl sm:text-3xl uppercase text-white mb-6 sm:mb-12">Continue Reading</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map(function(rp) {
                return (
                  <Link key={rp.slug} to={"/blog/" + rp.slug} className="border border-zinc-900 hover:border-amber-500 transition-colors group">
                    <div className="p-4">
                      <h3 className="font-mono text-sm text-white group-hover:text-amber-500 transition-colors">{rp.title}</h3>
                      <p className="font-mono text-[10px] text-zinc-500 mt-2">{rp.date}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
